import { createSelector } from 'reselect'
import { entitySelector, resultsSelector } from 'containers/Entity/selectors'
import { getMonthDateYearTimestampString } from 'helpers/DateUtil'

const cpmDetailsEntitySelector = createSelector(
  entitySelector,
  (entities) => entities.project_cpm_detail_views
    ? entities.project_cpm_detail_views
    : {}
)

const projectCpmSelector = state => state.project.projectCpm

const cpmDetailsChangeSelector = createSelector(
  projectCpmSelector,
  (projectCpm) => projectCpm.cpmDetailsChange
  ? projectCpm.cpmDetailsChange
  : {}
)

const cpmDetailsSelectionEntitySelector = createSelector(
  entitySelector,
  (entities) => entities.cpm_details_selections
    ? entities.cpm_details_selections
    : {}
)

const cpmDetailsSelector = createSelector(
  cpmDetailsEntitySelector,
  (entity) => {
    if(Object.values(entity)[0]){
      return Object.values(entity)[0]
    } else {
      return {}
    }
  }
)

const cpmNineHundredLevelPartNumberSelector = createSelector(
  cpmDetailsChangeSelector,
  (cpmDetailsChange) => cpmDetailsChange.nineHundredLevelPartNumber != null
  ? cpmDetailsChange.nineHundredLevelPartNumber
  : ''
)

const cpmProjectPriorityIdSelector = createSelector(
  cpmDetailsChangeSelector,
  (cpmDetailsChange) => cpmDetailsChange.projectPriorityId != null
  ? cpmDetailsChange.projectPriorityId
  : ''
)

const cpmProductSampledIdSelector = createSelector(
  cpmDetailsChangeSelector,
  (cpmDetailsChange) => cpmDetailsChange.productSampledId != null
  ? cpmDetailsChange.productSampledId
  : ''
)

const cpmReadyForReviewIdSelector = createSelector(
  cpmDetailsChangeSelector,
  (cpmDetailsChange) => cpmDetailsChange.readyForReviewId != null
  ? cpmDetailsChange.readyForReviewId
  : ''
)

const cpmPublishToWebIdSelector = createSelector(
  cpmDetailsChangeSelector,
  (cpmDetailsChange) => cpmDetailsChange.publishToWebId != null
  ? cpmDetailsChange.publishToWebId
  : ''
)

const generalAvailabilitySelector = createSelector(
  cpmDetailsChangeSelector,
  (cpmDetailsChange) => cpmDetailsChange.generalAvailability!= null
  ? cpmDetailsChange.generalAvailability
  : ''
)
const prodReleaseSelector = createSelector(
  cpmDetailsChangeSelector,
  (cpmDetailsChange) => cpmDetailsChange.prodRelease!= null
  ? cpmDetailsChange.prodRelease
  : ''
)
const cpmNotesSelector = createSelector(
  cpmDetailsChangeSelector,
  (cpmDetailsChange) => cpmDetailsChange.cpmNotes != null
  ? cpmDetailsChange.cpmNotes
  : ''
)

const cpmDetailsLastUpdatedNameSelector = createSelector(
  cpmDetailsChangeSelector,
  (cpmDetailsChange) => cpmDetailsChange.lastUpdateUserName
  ? cpmDetailsChange.lastUpdateUserName
  : ''
)

const cpmDetailsLastUpdatedDateSelector = createSelector(
  cpmDetailsChangeSelector,
  (cpmDetailsChange) => cpmDetailsChange.updatedAt
  ? getMonthDateYearTimestampString(new Date(cpmDetailsChange.updatedAt))
  : ''
)

const projectPriorityOptionsSelector = createSelector(
  cpmDetailsSelectionEntitySelector,
  (entity) => {
    if(Object.values(entity)[0] && Object.values(entity)[0].cpmProjectPrioritySources){
      var projectPrioritySources = Object.values(entity)[0].cpmProjectPrioritySources
      var toReturn = []
      Object.values(projectPrioritySources).forEach((projectPrioritySource) => {
        toReturn.push({ value: projectPrioritySource.id, label: projectPrioritySource.name })
      })
      return toReturn
    } else {
      return []
    }
  }
)

const productSampledOptionsSelector = createSelector(
  cpmDetailsSelectionEntitySelector,
  (entity) => {
    if(Object.values(entity)[0] && Object.values(entity)[0].cpmProductSampledSources){
      var productSampledSources = Object.values(entity)[0].cpmProductSampledSources
      var toReturn = []
      Object.values(productSampledSources).forEach((productSampledSource) => {
        toReturn.push({ value: productSampledSource.id, label: productSampledSource.name })
      })
      return toReturn
    } else {
      return []
    }
  }
)

const readyForReviewOptionsSelector = createSelector(
  cpmDetailsSelectionEntitySelector,
  (entity) => {
    if(Object.values(entity)[0] && Object.values(entity)[0].cpmReadyForReviewSources){
      var readyForReviewSources = Object.values(entity)[0].cpmReadyForReviewSources
      var toReturn = []
      Object.values(readyForReviewSources).forEach((readyForReviewSource) => {
        toReturn.push({ value: readyForReviewSource.id, label: readyForReviewSource.name })
      })
      return toReturn
    } else {
      return []
    }
  }
)

const publishToWebOptionsSelector = createSelector(
  cpmDetailsSelectionEntitySelector,
  (entity) => {
    if(Object.values(entity)[0] && Object.values(entity)[0].cpmPublishToWebSources){
      var publishToWebSources = Object.values(entity)[0].cpmPublishToWebSources
      var toReturn = []
      Object.values(publishToWebSources).forEach((publishToWebSource) => {
        toReturn.push({ value: publishToWebSource.id, label: publishToWebSource.name })
      })
      return toReturn
    } else {
      return []
    }
  }
)

const cpmDetailsParamsSelector = createSelector(
  cpmNineHundredLevelPartNumberSelector,
  cpmProjectPriorityIdSelector,
  cpmProductSampledIdSelector,
  cpmReadyForReviewIdSelector,
  cpmPublishToWebIdSelector,
  cpmNotesSelector,
  generalAvailabilitySelector,
  prodReleaseSelector,
  (nineHundredLevelPartNumber, projectPriorityId, productSampledId, readyForReviewId, publishToWebId, cpmNotes, generalAvailability, prodRelease) => ({
    nineHundredLevelPartNumber: nineHundredLevelPartNumber,
    projectPrioritySourceId: projectPriorityId,
    productSampledSourceId: productSampledId,
    readyForReviewSourceId: readyForReviewId,
    publishToWebSourceId: publishToWebId,
    cpmNotes: cpmNotes,
    generalAvailability: generalAvailability,
    prodRelease: prodRelease
  })
)

const cpmDetailsLoadingSelector = createSelector(
  projectCpmSelector,
  (projectCpm) => projectCpm.loading
)

export {
  cpmDetailsSelector,
  cpmDetailsEntitySelector,
  cpmDetailsLastUpdatedNameSelector,
  cpmDetailsLastUpdatedDateSelector,
  cpmDetailsLoadingSelector,
  cpmNineHundredLevelPartNumberSelector,
  cpmProjectPriorityIdSelector,
  cpmProductSampledIdSelector,
  cpmReadyForReviewIdSelector,
  cpmPublishToWebIdSelector,
  cpmNotesSelector,
  projectPriorityOptionsSelector,
  productSampledOptionsSelector,
  readyForReviewOptionsSelector,
  publishToWebOptionsSelector,
  cpmDetailsParamsSelector,
  prodReleaseSelector,
  generalAvailabilitySelector
}
